import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { InputStyle } from 'module/ui/components/atoms/input/input';
import { SingleLineEllipsis } from 'module/ui/components/styles';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import {
  PopoverSelect,
  type PopoverSelectGroup,
  type PopoverSelectOption,
} from '@rocketlane/ui';
import { PageTemplateLite, PageType } from 'api/page-template/types';
import { keyBy, snakeCase } from 'lodash';
import { ReactNode, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

const TriggerPlaceholder = styled.div`
  color: var(--text-placeholder);
  ${SingleLineEllipsis};
`;

const TriggerContent = styled.div`
  flex: 1;
  min-width: 0;
  ${SingleLineEllipsis};
`;

const Divider = styled.div`
  width: 1px;
  height: 16px;
  margin: 0 var(--spacing-03);
  background-color: var(--field-border-default);
`;

const Trigger = styled.button.attrs({
  className: 'custom-input',
  type: 'button',
})`
  ${InputStyle};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-03);
  cursor: pointer;

  > ${FontAwesomeIcon} {
    color: var(--icon-secondary);
  }
`;

const SelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

type TemplateByPage = {
  [key in PageType]: PageTemplateLite[];
};
interface Props {
  templatesByPage: Partial<TemplateByPage>;
  value?: PageTemplateLite[];
  onChange?: (value: PageTemplateLite[]) => void;
  triggerContent?: ReactNode;
  isLoading?: boolean;
  renderGroupAction?: React.ComponentProps<
    typeof PopoverSelect
  >['renderGroupAction'];
}
const templateAsOption = (template: PageTemplateLite): PopoverSelectOption => ({
  label: template.templateName,
  value: template.templateId,
});

export const PageTemplateSourceSelector = ({
  templatesByPage,
  value: templates,
  onChange: onTemplatesChange,
  triggerContent,
  isLoading,
  renderGroupAction,
}: Props) => {
  const intl = useIntl();
  const title = intl.formatMessage({
    id: 'company_page_templates.fields.source.label',
  });
  const placeholder = intl.formatMessage({
    id: 'company_page_templates.fields.source.placeholder',
  });
  const searchPlaceholder = intl.formatMessage({ id: 'search' });
  const doneLabel = intl.formatMessage({ id: 'done' });
  const allTemplatesById = useMemo(() => {
    return keyBy(Object.values(templatesByPage).flat(), 'templateId');
  }, [templatesByPage]);
  const groups = useMemo<PopoverSelectGroup[]>(() => {
    return Object.entries(templatesByPage).map(([pageType, templates]) => {
      return {
        key: pageType,
        label: intl.formatMessage({
          id: `page_template.type.${snakeCase(pageType)}.label`,
        }),
        items: templates.map(templateAsOption),
      };
    });
  }, [intl, templatesByPage]);
  const value = useMemo(() => {
    return templates?.map(templateAsOption) ?? [];
  }, [templates]);
  const onChange = useCallback(
    (next: PopoverSelectOption[]) => {
      onTemplatesChange?.(
        next.map(({ value }) => allTemplatesById[value]).filter(Boolean),
      );
    },
    [allTemplatesById, onTemplatesChange],
  );

  return (
    <SelectorContainer>
      <PopoverSelect
        className="w-full"
        dropdownHeader={title}
        groups={groups}
        isMulti
        value={value}
        onChange={(next) => onChange(Array.isArray(next) ? next : [])}
        commitOnClose
        renderTrigger={() => (
          <Trigger aria-label={title}>
            <TriggerContent>
              {triggerContent ?? (
                <TriggerPlaceholder>{placeholder}</TriggerPlaceholder>
              )}
            </TriggerContent>
            <Divider />
            <FontAwesomeIcon icon={faChevronDown} size="xs" />
          </Trigger>
        )}
        searchPlaceholder={searchPlaceholder}
        doneLabel={doneLabel}
        widthMatchTrigger
        getOptionDataCy={(option) => `select-option-${option.label}`}
        isLoading={isLoading}
        renderGroupAction={renderGroupAction}
      />
    </SelectorContainer>
  );
};
